/**
 * Lightweight i18n engine for Art of Thinking.
 *
 * Features:
 * - Flat key namespace with dot notation (e.g. "flow.lookup.title")
 * - {{variable}} interpolation
 * - Plural support via _plural suffix (count > 1)
 * - Automatic fallback: current locale → 'en' → raw key
 */

type Translations = Record<string, string>;
type InterpolationValues = Record<string, string | number>;

const bundles: Record<string, Translations> = {};
let currentLocale = 'en';

// Set of locale codes that have a translation bundle loaded
const loadedLocales = new Set<string>();

/** All locale codes that have translation bundles available. */
export const SUPPORTED_UI_LOCALES = ['en', 'fi', 'sv'] as const;
export type SupportedUILocale = (typeof SUPPORTED_UI_LOCALES)[number];

/** Check whether a locale code has a UI translation bundle. */
export function isUILocaleSupported(code: string): code is SupportedUILocale {
  return (SUPPORTED_UI_LOCALES as readonly string[]).includes(code);
}

/** Native display names for supported UI locales. */
export const LOCALE_NATIVE_NAMES: Record<SupportedUILocale, string> = {
  en: 'English',
  fi: 'Suomi',
  sv: 'Svenska',
};

// ---------------------------------------------------------------------------
// Core API
// ---------------------------------------------------------------------------

/** Load a translation bundle for the given locale.
 *  Accepts the raw JSON import which may include a `_meta` object — non-string
 *  entries are silently skipped so that only real translation keys are stored.
 */
export function loadTranslations(
  locale: string,
  data: Record<string, unknown>,
): void {
  const cleaned: Translations = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      cleaned[key] = value;
    }
  }
  bundles[locale] = { ...bundles[locale], ...cleaned };
  loadedLocales.add(locale);
}

/** Set the active locale. Falls back to 'en' if unknown. */
export function setLocale(locale: string): void {
  currentLocale = loadedLocales.has(locale) ? locale : 'en';
}

/** Get the currently active locale code. */
export function getLocale(): string {
  return currentLocale;
}

/**
 * Translate a key with optional interpolation values.
 *
 * Behaviour:
 * 1. If `values.count` is a number > 1, tries `key_plural` first.
 * 2. Looks up the key in the current locale's bundle.
 * 3. Falls back to the 'en' bundle.
 * 4. Falls back to the raw key string.
 * 5. Replaces all `{{name}}` tokens with their corresponding values.
 */
export function t(key: string, values?: InterpolationValues): string {
  const count = values?.count;
  const usePlural = typeof count === 'number' && count !== 1;

  const resolve = (k: string): string | undefined =>
    bundles[currentLocale]?.[k] ?? bundles.en?.[k];

  let text: string | undefined;

  if (usePlural) {
    text = resolve(`${key}_plural`);
  }

  if (text === undefined) {
    text = resolve(key);
  }

  // Ultimate fallback — return the key itself so missing translations are visible during dev.
  if (text === undefined) {
    return key;
  }

  // Interpolation
  if (values) {
    text = text.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => {
      const val = values[name];
      return val !== undefined ? String(val) : `{{${name}}}`;
    });
  }

  return text;
}
